/**
 * Phase76AuthContext
 * ─────────────────────────────────────────────────────────────────────────────
 * Centralized React context for the Phase 76 RBAC surface. Hydrates ONCE per
 * session and caches per-company permission arrays so components never re-call
 * the backend for the same authorization decision.
 *
 * Lives BESIDE the legacy AuthContext / CompanyContext — never replaces them.
 * Components migrate to the new hooks one-by-one.
 *
 * Hydration flow (kicked off on auth state change)
 * ────────────────────────────────────────────────
 *   1. getMyCompaniesWithModules()            → companies + per-company
 *      active module subscription keys, in a SINGLE round-trip
 *      (Phase 76.2.E). Replaces the legacy per-active-company
 *      `companyHasModule` probe loop for the known-module catalog.
 *   2. is_super_admin()                       → isSuperAdmin
 *      (We derive isSuperAdmin from `is_super_admin()` RPC explicitly, NOT
 *      from the synthesized 'owner' membership_type, to keep the boolean
 *      semantically clean.)
 *   3. For the active company:
 *        getMyPermissionsForCompany(id)       → permissionsByCompany[id]
 *   4. `subscribedModulesByCompany` is hydrated authoritatively from step 1
 *      for ALL companies, then union-merged with permission-prefix derivation
 *      from step 3 for the active company (best-effort safety net).
 *
 * Active company selection
 * ────────────────────────
 *   Reads/writes the same localStorage key the legacy CompanyContext uses
 *   ('axelio_active_company_id') so the two contexts stay aligned during the
 *   evolutionary cutover. If the legacy context changes the active company,
 *   call refreshActiveCompanyPermissions() from the consumer side, or simply
 *   rely on the storage event listener.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/features/auth';
import {
  phase76,
  modulePrefixOf,
  type MyCompany,
  type MyCompanyWithModules,
  type MembershipType,
  type Phase76Permission,
  type Phase76ModuleKey,
} from './phase76Client';
// Phase 77 — every EXPLICIT `setActiveCompanyId` call flips the
// per-tab workspace-selection flag so `<PostLoginGate>` allows
// `/app/*` entry. Fresh logins start un-flagged → forced to /workspaces.
import { markWorkspaceSelected } from './workspaceSession';

// ─── Storage key (shared with legacy CompanyContext for evolutionary parity) ─

const STORAGE_KEY = 'axelio_active_company_id';

// ─── Context shape ───────────────────────────────────────────────────────────

export interface Phase76AuthState {
  /** True once the initial hydration pass completes (success OR failure). */
  hydrated:                    boolean;
  /** True while a hydration pass is in flight. */
  loading:                     boolean;
  /** Last hydration error, if any. */
  error:                       Error | null;

  /** From is_super_admin() RPC — strictly pinned to 972cfe@gmail.com server-side. */
  isSuperAdmin:                boolean;

  /** Companies the caller belongs to (super-admin sees ALL with synthesized 'owner'). */
  companies:                   MyCompany[];

  /** UUID of the active company (synced with legacy 'axelio_active_company_id'). */
  activeCompanyId:             string | null;

  /** The MyCompany row for the active id, if any. */
  activeCompany:               MyCompany | null;

  /** Per-company permission caches. */
  permissionsByCompany:        Record<string, Set<string>>;

  /**
   * Per-company subscribed-module caches.
   * Derived from `permissionsByCompany` via module-prefix extraction +
   * an explicit prefetch when we need to know "is X subscribed?" without
   * a permission grant (e.g. for the grey-out guard in the permissions
   * matrix when viewed by a super-admin).
   */
  subscribedModulesByCompany:  Record<string, Set<string>>;

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Change the active company (persists to localStorage). */
  setActiveCompanyId:          (id: string | null) => void;

  /** Force a full refresh (companies + active permissions). */
  refresh:                     () => Promise<void>;

  /** Force a refresh of permissions for the active company only. */
  refreshActiveCompanyPermissions: () => Promise<void>;

  /** Server-evaluated subscription check, cached by (companyId, moduleKey). */
  refreshSubscribedModules:    (companyId: string) => Promise<void>;

  // ── Synchronous local checks (against cached state, active company) ──────

  hasPermissionLocal:          (perm: Phase76Permission | string) => boolean;
  hasModuleLocal:              (moduleKey: Phase76ModuleKey | string) => boolean;

  /** Synchronous local check for a SPECIFIC company (uses cached data). */
  hasPermissionInCompany:      (companyId: string, perm: Phase76Permission | string) => boolean;
  hasModuleInCompany:          (companyId: string, moduleKey: Phase76ModuleKey | string) => boolean;

  /** Synchronous membership-type lookup from the cached companies list. */
  membershipTypeFor:           (companyId: string) => MembershipType | null;

  /** True for owner / co_owner in the given (or active) company. */
  isCompanyOwnerOrCoOwnerLocal:(companyId?: string) => boolean;
}

const Phase76AuthCtx = createContext<Phase76AuthState | undefined>(undefined);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function readStoredCompanyId(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeStoredCompanyId(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / privacy errors */
  }
}

function setFromArray(arr: string[] | null | undefined): Set<string> {
  return new Set((arr ?? []).filter(Boolean));
}

function derivePrefixSet(perms: Set<string>): Set<string> {
  const out = new Set<string>();
  perms.forEach((p) => out.add(modulePrefixOf(p)));
  return out;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function Phase76AuthProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();

  const [hydrated,         setHydrated]         = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<Error | null>(null);
  const [isSuperAdminFlag, setIsSuperAdminFlag] = useState(false);
  const [companies,        setCompanies]        = useState<MyCompany[]>([]);
  const [activeCompanyId,  setActiveCompanyId]  = useState<string | null>(() =>
    readStoredCompanyId(),
  );
  const [permissionsByCompany, setPermissionsByCompany] = useState<Record<string, Set<string>>>(
    {},
  );
  const [subscribedModulesByCompany, setSubscribedModulesByCompany] = useState<
    Record<string, Set<string>>
  >({});

  // Prevent overlapping hydrations from stomping on each other.
  const hydrationToken = useRef(0);

  // ── Internal: load permissions for a given company id ────────────────────
  const loadPermissionsFor = useCallback(async (companyId: string) => {
    const perms = await phase76.getMyPermissionsForCompany(companyId);
    const set   = setFromArray(perms);
    setPermissionsByCompany((prev) => ({ ...prev, [companyId]: set }));
    // Derive module subscriptions from permission prefixes too — best-effort.
    // (The dedicated refreshSubscribedModules() call below is authoritative.)
    setSubscribedModulesByCompany((prev) => {
      const prevMods = prev[companyId] ?? new Set<string>();
      const merged   = new Set<string>(prevMods);
      derivePrefixSet(set).forEach((m) => merged.add(m));
      return { ...prev, [companyId]: merged };
    });
    return set;
  }, []);

  // ── Internal: authoritative subscription refresh for a SINGLE company ────
  // Phase 76.2.E: instead of the legacy per-module `companyHasModule` probe
  // loop, we re-call the new bulk RPC and extract the single company's row.
  // This keeps the catalog open-ended (no hardcoded KNOWN_MODULES list) and
  // always reflects whatever the server says, including modules seeded after
  // the app was built.
  const refreshSubscribedModules = useCallback(async (companyId: string) => {
    if (!companyId) return;
    try {
      const rows = await phase76.getMyCompaniesWithModules();
      const row  = rows.find((r) => r.company_id === companyId);
      const next = new Set<string>(row?.module_keys ?? []);
      setSubscribedModulesByCompany((prev) => ({ ...prev, [companyId]: next }));
    } catch (e) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[Phase76AuthContext] refreshSubscribedModules failed:', e);
      }
    }
  }, []);

  // ── Internal: bulk hydration of subscribedModulesByCompany for ALL companies
  // Used during the main `refresh()` pass. Builds the cross-company module
  // availability map in one round-trip via `get_my_companies_with_modules()`.
  const hydrateAllSubscribedModules = useCallback(
    (rows: MyCompanyWithModules[]) => {
      const next: Record<string, Set<string>> = {};
      for (const r of rows) {
        next[r.company_id] = new Set<string>(r.module_keys ?? []);
      }
      setSubscribedModulesByCompany(next);
    },
    [],
  );

  // ── Full hydration pass ───────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!user?.id) {
      // Signed out — clear state and mark hydrated so guards stop spinning.
      setIsSuperAdminFlag(false);
      setCompanies([]);
      setPermissionsByCompany({});
      setSubscribedModulesByCompany({});
      setHydrated(true);
      setLoading(false);
      setError(null);
      return;
    }
    const token = ++hydrationToken.current;
    setLoading(true);
    setError(null);
    try {
      // Phase 76.2.E: single bulk RPC for companies + per-company modules.
      const [sa, cosWithModules] = await Promise.all([
        phase76.isSuperAdmin().catch(() => false),
        phase76.getMyCompaniesWithModules().catch((e) => {
          throw e;
        }),
      ]);
      if (token !== hydrationToken.current) return;  // a newer pass superseded us

      setIsSuperAdminFlag(Boolean(sa));

      // Derive the lean MyCompany[] for the public `companies` field, and
      // populate cross-company module availability from the same payload.
      const cos: MyCompany[] = cosWithModules.map(({ module_keys: _ignored, ...rest }) => rest);
      setCompanies(cos);
      hydrateAllSubscribedModules(cosWithModules);

      // Restore / coerce active company id
      const stored = readStoredCompanyId();
      const valid  = stored && cos.some((c) => c.company_id === stored)
        ? stored
        : (cos[0]?.company_id ?? null);
      if (valid !== activeCompanyId) {
        setActiveCompanyId(valid);
        writeStoredCompanyId(valid);
      }

      // Load permissions for the active company. Subscription set is already
      // populated above for ALL companies, so no per-company probe needed.
      if (valid) {
        await loadPermissionsFor(valid);
      }
    } catch (e) {
      if (token === hydrationToken.current) {
        const err = e instanceof Error ? e : new Error(String(e));
        // eslint-disable-next-line no-console
        console.error('[Phase76AuthContext] refresh error:', err);
        setError(err);
      }
    } finally {
      if (token === hydrationToken.current) {
        setLoading(false);
        setHydrated(true);
      }
    }
  // We intentionally omit `activeCompanyId` from deps — it's read inside but
  // managed within the same effect and would otherwise loop on every change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loadPermissionsFor, hydrateAllSubscribedModules]);

  const refreshActiveCompanyPermissions = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      await Promise.all([
        loadPermissionsFor(activeCompanyId),
        refreshSubscribedModules(activeCompanyId),
      ]);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      // eslint-disable-next-line no-console
      console.error('[Phase76AuthContext] refreshActiveCompanyPermissions error:', err);
      setError(err);
    }
  }, [activeCompanyId, loadPermissionsFor, refreshSubscribedModules]);

  // ── React to auth changes ─────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, user?.id, refresh]);

  // ── Sync with the legacy CompanyContext via storage events ────────────────
  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === STORAGE_KEY) {
        const next = ev.newValue && ev.newValue.length > 0 ? ev.newValue : null;
        setActiveCompanyId(next);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── When activeCompanyId changes, ensure perms+subs are loaded ────────────
  // Phase 76.2.E: `subscribedModulesByCompany` is hydrated for ALL companies
  // during `refresh()`, so this effect typically only needs to fetch the
  // per-company permission set on its first miss. We still re-probe the
  // module subscriptions for the newly-active company if missing (e.g. when
  // the storage event handler set an id we haven't yet seen), but in the
  // common case this is a no-op.
  useEffect(() => {
    if (!user?.id || !activeCompanyId) return;
    if (permissionsByCompany[activeCompanyId] && subscribedModulesByCompany[activeCompanyId]) {
      return;
    }
    (async () => {
      try {
        await Promise.all([
          permissionsByCompany[activeCompanyId]
            ? Promise.resolve()
            : loadPermissionsFor(activeCompanyId),
          subscribedModulesByCompany[activeCompanyId]
            ? Promise.resolve()
            : refreshSubscribedModules(activeCompanyId),
        ]);
      } catch (e) {
        // Errors already logged in callees.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.debug('[Phase76AuthContext] activeCompany effect error:', e);
        }
      }
    })();
  // permissionsByCompany / subscribedModulesByCompany are stable per-id;
  // re-running on every cache write would cause loops. The internal guards
  // above already short-circuit when the entry exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeCompanyId, loadPermissionsFor, refreshSubscribedModules]);

  // ── Public action: setActiveCompanyId (persisted) ────────────────────────
  // Phase 77: any explicit, user-triggered company selection (Workspaces
  // portal click, ModuleGuard auto-switch, UnauthorizedModulePage picker,
  // OnboardingPage completion, JoinPage invite acceptance) flips the
  // session flag here. This is the central choke point — by anchoring the
  // flag mutation on the setter we avoid having to remember to mark each
  // call site individually.
  const setActiveCompanyIdPublic = useCallback((id: string | null) => {
    setActiveCompanyId(id);
    writeStoredCompanyId(id);
    if (id) markWorkspaceSelected();
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeCompany = useMemo<MyCompany | null>(
    () => companies.find((c) => c.company_id === activeCompanyId) ?? null,
    [companies, activeCompanyId],
  );

  // ── Sync local checks ─────────────────────────────────────────────────────
  const hasPermissionInCompany = useCallback(
    (companyId: string, perm: Phase76Permission | string): boolean => {
      if (!companyId || !perm) return false;
      // Super-admin short-circuit mirrors server-side has_permission().
      if (isSuperAdminFlag) return true;
      const set = permissionsByCompany[companyId];
      return set ? set.has(perm) : false;
    },
    [isSuperAdminFlag, permissionsByCompany],
  );

  const hasModuleInCompany = useCallback(
    (companyId: string, moduleKey: Phase76ModuleKey | string): boolean => {
      if (!companyId || !moduleKey) return false;
      const set = subscribedModulesByCompany[companyId];
      return set ? set.has(moduleKey) : false;
    },
    [subscribedModulesByCompany],
  );

  const hasPermissionLocal = useCallback(
    (perm: Phase76Permission | string): boolean =>
      activeCompanyId ? hasPermissionInCompany(activeCompanyId, perm) : false,
    [activeCompanyId, hasPermissionInCompany],
  );

  const hasModuleLocal = useCallback(
    (moduleKey: Phase76ModuleKey | string): boolean =>
      activeCompanyId ? hasModuleInCompany(activeCompanyId, moduleKey) : false,
    [activeCompanyId, hasModuleInCompany],
  );

  const membershipTypeFor = useCallback(
    (companyId: string): MembershipType | null => {
      const row = companies.find((c) => c.company_id === companyId);
      return row?.membership_type ?? null;
    },
    [companies],
  );

  const isCompanyOwnerOrCoOwnerLocal = useCallback(
    (companyId?: string): boolean => {
      if (isSuperAdminFlag) return true;
      const id = companyId ?? activeCompanyId ?? '';
      if (!id) return false;
      const mt = membershipTypeFor(id);
      return mt === 'owner' || mt === 'co_owner';
    },
    [isSuperAdminFlag, activeCompanyId, membershipTypeFor],
  );

  const value = useMemo<Phase76AuthState>(
    () => ({
      hydrated,
      loading,
      error,
      isSuperAdmin: isSuperAdminFlag,
      companies,
      activeCompanyId,
      activeCompany,
      permissionsByCompany,
      subscribedModulesByCompany,
      setActiveCompanyId: setActiveCompanyIdPublic,
      refresh,
      refreshActiveCompanyPermissions,
      refreshSubscribedModules,
      hasPermissionLocal,
      hasModuleLocal,
      hasPermissionInCompany,
      hasModuleInCompany,
      membershipTypeFor,
      isCompanyOwnerOrCoOwnerLocal,
    }),
    [
      hydrated,
      loading,
      error,
      isSuperAdminFlag,
      companies,
      activeCompanyId,
      activeCompany,
      permissionsByCompany,
      subscribedModulesByCompany,
      setActiveCompanyIdPublic,
      refresh,
      refreshActiveCompanyPermissions,
      refreshSubscribedModules,
      hasPermissionLocal,
      hasModuleLocal,
      hasPermissionInCompany,
      hasModuleInCompany,
      membershipTypeFor,
      isCompanyOwnerOrCoOwnerLocal,
    ],
  );

  return <Phase76AuthCtx.Provider value={value}>{children}</Phase76AuthCtx.Provider>;
}

// ─── Hook: usePhase76Auth ────────────────────────────────────────────────────

export function usePhase76Auth(): Phase76AuthState {
  const ctx = useContext(Phase76AuthCtx);
  if (!ctx) {
    throw new Error(
      'usePhase76Auth must be used within <Phase76AuthProvider>. ' +
      'Wrap your app tree (typically in App.tsx) with the provider.',
    );
  }
  return ctx;
}
